import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useSuppressTutorialTips } from '../../context/TutorialContext';
import PlayerCard from './PlayerCard';
import PlayerCardMini from './PlayerCardMini';
import { normalizeCardPlayer, tierOf } from './cardUtils';
import { useTransferRevealSuppression } from './TransferRevealContext';
import {
  markSquadRevealSeen,
  rankSquadPlayers,
  SQUAD_REVEAL_STAGES as STAGES,
} from './squadRevealUtils';

const POSITION_ROW_ORDER = ['FWD', 'MID', 'DEF', 'GKP'];

function countTiers(entries) {
  return entries.reduce((counts, entry) => {
    const normalized = entry.normalized || normalizeCardPlayer(entry.player || entry);
    const tier = tierOf(normalized.overall);
    if (tier) {
      counts[tier] += 1;
    } else {
      counts.unrated += 1;
    }
    return counts;
  }, { gold: 0, silver: 0, bronze: 0, unrated: 0 });
}

function averageOverall(entries) {
  const values = entries
    .map((entry) => (entry.normalized || normalizeCardPlayer(entry.player || entry)).overall)
    .filter((overall) => overall !== null);

  if (values.length === 0) return '-';
  return Math.round(values.reduce((sum, overall) => sum + overall, 0) / values.length);
}

function getBestXi(rankedPlayers, startingXi) {
  const starters = Array.isArray(startingXi)
    ? startingXi.filter(Boolean).slice(0, 11)
    : [];

  if (starters.length === 11) {
    return starters.map((player, index) => ({
      player,
      normalized: normalizeCardPlayer(player),
      originalIndex: index,
    }));
  }

  return rankedPlayers.slice(0, 11);
}

function groupBestXi(bestXi) {
  const grouped = new Map(POSITION_ROW_ORDER.map((group) => [group, []]));

  bestXi.forEach((entry) => {
    const group = POSITION_ROW_ORDER.includes(entry.normalized.positionGroup)
      ? entry.normalized.positionGroup
      : 'MID';
    grouped.get(group).push(entry);
  });

  return POSITION_ROW_ORDER
    .map((group) => ({ group, entries: grouped.get(group) }))
    .filter((row) => row.entries.length > 0);
}

function TierSummary({ counts, prefix }) {
  return (
    <div className="squad-reveal__sumbar" aria-label={`${prefix} card tiers`}>
      <span>{prefix}</span>
      <span className="squad-reveal__tier squad-reveal__tier--gold"><b>{counts.gold}</b> gold</span>
      <span className="squad-reveal__tier squad-reveal__tier--silver"><b>{counts.silver}</b> silver</span>
      <span className="squad-reveal__tier squad-reveal__tier--bronze"><b>{counts.bronze}</b> bronze</span>
      {counts.unrated > 0 ? <span><b>{counts.unrated}</b> unrated</span> : null}
    </div>
  );
}

function HeadlinerStage({ eyebrow, entry, caption, animationClass }) {
  return (
    <div className="squad-reveal__stage squad-reveal__stage--card">
      <p className="squad-reveal__eyebrow">{eyebrow}</p>
      <div className="squad-reveal__single-card">
        <PlayerCard
          player={entry.player}
          size="lg"
          className={`squad-reveal__big-card ${animationClass}`}
        />
      </div>
      <p className="squad-reveal__caption">{caption} <b>{entry.normalized.name}</b></p>
    </div>
  );
}

function SquadReveal({
  teamId,
  teamName,
  leagueName,
  players,
  startingXi,
  formationName,
  onClose,
}) {
  const [stage, setStage] = useState(STAGES.intro);
  const rankedPlayers = useMemo(() => rankSquadPlayers(players), [players]);
  const headliners = rankedPlayers.slice(0, 3);
  const rest = rankedPlayers.slice(3);
  const restCounts = useMemo(() => countTiers(rest), [rest]);
  const fullCounts = useMemo(() => countTiers(rankedPlayers), [rankedPlayers]);
  const bestXi = useMemo(
    () => getBestXi(rankedPlayers, startingXi),
    [rankedPlayers, startingXi]
  );
  const bestXiRows = useMemo(() => groupBestXi(bestXi), [bestXi]);
  const substitutes = useMemo(() => rankedPlayers.filter((entry) => (
    !bestXi.some((bestXiEntry) => {
      const entryWyId = entry.normalized.wyId;
      const bestXiWyId = bestXiEntry.normalized.wyId;

      if (Number.isFinite(entryWyId) && Number.isFinite(bestXiWyId)) {
        return entryWyId === bestXiWyId;
      }

      return entry === bestXiEntry || entry.player === bestXiEntry.player;
    })
  )), [bestXi, rankedPlayers]);
  const substituteDelayStart = useMemo(() => bestXiRows.reduce(
    (latest, row, rowIndex) => Math.max(latest, rowIndex * 4 + row.entries.length),
    0
  ), [bestXiRows]);
  const { setSquadRevealOpen } = useTransferRevealSuppression();

  useSuppressTutorialTips(true);

  useLayoutEffect(() => {
    setSquadRevealOpen(true);
    return () => setSquadRevealOpen(false);
  }, [setSquadRevealOpen]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const rememberSeen = useCallback(() => {
    markSquadRevealSeen(teamId);
  }, [teamId]);

  const exitReveal = useCallback(() => {
    rememberSeen();
    onClose?.();
  }, [onClose, rememberSeen]);

  const advanceStage = useCallback(() => {
    setStage((currentStage) => {
      if (currentStage >= STAGES.bestXi) return currentStage;
      const nextStage = currentStage + 1;
      if (nextStage === STAGES.bestXi) {
        rememberSeen();
      }
      return nextStage;
    });
  }, [rememberSeen]);

  const skipToSquad = useCallback((event) => {
    event.stopPropagation();
    rememberSeen();
    setStage(STAGES.rest);
  }, [rememberSeen]);

  const handleClose = useCallback((event) => {
    event.stopPropagation();
    exitReveal();
  }, [exitReveal]);

  const handleAction = useCallback((event) => {
    event.stopPropagation();
    advanceStage();
  }, [advanceStage]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      exitReveal();
    }
  }, [exitReveal]);

  if (headliners.length < 3) return null;

  const thirdBest = headliners[2];
  const secondBest = headliners[1];
  const walkout = headliners[0];
  const walkoutNationality = walkout.normalized.nationalityName
    || walkout.normalized.nationalityCode
    || 'Unknown';
  const walkoutPosition = walkout.normalized.positionGroup
    || walkout.normalized.positionLabel
    || 'Unknown';
  const walkoutAge = walkout.normalized.age ?? 'Unknown';
  const fullAverage = averageOverall(rankedPlayers);
  const bestXiAverage = averageOverall(bestXi);
  const stageLabels = [
    '',
    'Headliner 1 of 3',
    'Headliner 2 of 3',
    'Headliner 3 of 3',
    'Walkout',
    `The other ${rest.length}`,
    '',
  ];

  return (
    <div
      className={`squad-reveal squad-reveal--stage-${stage}`}
      role="dialog"
      aria-modal="true"
      aria-label="Squad reveal"
      onClick={stage < STAGES.bestXi ? advanceStage : undefined}
      onKeyDown={handleKeyDown}
    >
      <div className="squad-reveal__ambient" aria-hidden="true" />
      {(stageLabels[stage] || stage < STAGES.rest) ? (
        <div className="squad-reveal__header">
          {stageLabels[stage] ? (
            <p className="squad-reveal__count">{stageLabels[stage]}</p>
          ) : null}
          {stage < STAGES.rest ? (
            <button type="button" className="squad-reveal__skip" onClick={skipToSquad}>
              Skip reveal
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="squad-reveal__body">
        {stage === STAGES.intro ? (
          <div className="squad-reveal__stage squad-reveal__stage--intro">
            <p className="squad-reveal__eyebrow">{leagueName || 'Your league'}</p>
            <h2>Your squad is <em>ready</em></h2>
            <p className="squad-reveal__subcopy">
              {rankedPlayers.length} players are ready for {teamName || 'your team'}. Meet your three headliners first, with the best saved for last.
            </p>
            <button type="button" className="squad-reveal__button" onClick={handleAction}>
              REVEAL MY SQUAD
            </button>
          </div>
        ) : null}

        {stage === STAGES.thirdBest ? (
          <HeadlinerStage
            eyebrow="First out of the pack"
            entry={thirdBest}
            caption="Headliner:"
            animationClass="squad-reveal__flip-in"
          />
        ) : null}

        {stage === STAGES.secondBest ? (
          <HeadlinerStage
            eyebrow="The pack gets stronger"
            entry={secondBest}
            caption="Headliner:"
            animationClass="squad-reveal__flip-in"
          />
        ) : null}

        {stage === STAGES.tease ? (
          <div className="squad-reveal__stage squad-reveal__stage--tease">
            <p className="squad-reveal__eyebrow">One more. The big one</p>
            <h2>Your walkout is waiting</h2>
            <div className="squad-reveal__tease" aria-label="Walkout clues">
              <p style={{ '--squad-reveal-delay': '120ms' }}>Nationality: {walkoutNationality}</p>
              <p style={{ '--squad-reveal-delay': '620ms' }}>Position: {walkoutPosition}</p>
              <p style={{ '--squad-reveal-delay': '1120ms' }}>Age: {walkoutAge}</p>
            </div>
          </div>
        ) : null}

        {stage === STAGES.walkout ? (
          <div className="squad-reveal__stage squad-reveal__stage--walkout">
            <p className="squad-reveal__eyebrow">Your number one</p>
            <div className="squad-reveal__walkout">
              <div className="squad-reveal__rays" aria-hidden="true" />
              <PlayerCard
                player={walkout.player}
                size="lg"
                className="squad-reveal__big-card squad-reveal__slam-in"
              />
            </div>
            <p className="squad-reveal__caption">Walkout: <b>{walkout.normalized.name}</b></p>
          </div>
        ) : null}

        {stage === STAGES.rest ? (
          <div className="squad-reveal__stage squad-reveal__stage--rest">
            <p className="squad-reveal__eyebrow">Your headliners are out. Here is the depth</p>
            <h2>The other {rest.length}, <em>all yours</em></h2>
            <div className="squad-reveal__rest-grid" aria-label="Remaining squad players">
              {rest.map((entry, index) => (
                <div
                  key={`${entry.normalized.wyId ?? entry.originalIndex}-${entry.normalized.name}`}
                  className="squad-reveal__mini-slot"
                  style={{ '--squad-reveal-delay': `${index * 45}ms` }}
                >
                  <PlayerCardMini
                    player={entry.player}
                    showRating={false}
                    className="squad-reveal__mini-in"
                  />
                </div>
              ))}
            </div>
            <TierSummary counts={restCounts} prefix={`These ${rest.length}:`} />
            <p className="squad-reveal__headliner-note">
              Your 3 headliners, {headliners.map((entry) => entry.normalized.name).join(', ')}, already walked out.
            </p>
            <button type="button" className="squad-reveal__button" onClick={handleAction}>
              SEE MY BEST XI
            </button>
          </div>
        ) : null}

        {stage === STAGES.bestXi ? (
          <div className="squad-reveal__stage squad-reveal__stage--best-xi">
            <h2>Your best XI, <em>ready to play</em></h2>
            <div className="squad-reveal__pitch" aria-label="Best XI formation">
              <p className="squad-reveal__formation">{formationName || 'Best XI'}</p>
              {bestXiRows.map((row, rowIndex) => (
                <div className="squad-reveal__formation-row" key={row.group} data-position-group={row.group}>
                  {row.entries.map((entry, entryIndex) => (
                    <div
                      key={`${entry.normalized.wyId ?? entry.originalIndex}-${row.group}`}
                      className="squad-reveal__mini-slot"
                      style={{ '--squad-reveal-delay': `${(rowIndex * 4 + entryIndex) * 55}ms` }}
                    >
                      <PlayerCardMini
                        player={entry.player}
                        showRating={false}
                        className="squad-reveal__mini-in"
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {substitutes.length > 0 ? (
              <div className="squad-reveal__subs" aria-label="Substitutes">
                <p className="squad-reveal__subheading squad-reveal__subs-title">Substitutes</p>
                <div className="squad-reveal__subs-row">
                  {substitutes.map((entry, index) => (
                    <div
                      key={`${entry.normalized.wyId ?? entry.originalIndex}-substitute`}
                      className="squad-reveal__mini-slot"
                      style={{ '--squad-reveal-delay': `${(substituteDelayStart + index) * 55}ms` }}
                    >
                      <PlayerCardMini
                        player={entry.player}
                        showRating={false}
                        className="squad-reveal__mini-in"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="squad-reveal__sumbar squad-reveal__sumbar--averages">
              <span>Best XI average <b>{bestXiAverage}</b></span>
              <span>Full squad average <b>{fullAverage}</b></span>
            </div>
            <TierSummary counts={fullCounts} prefix="Full squad:" />
            <button type="button" className="squad-reveal__button" onClick={handleClose}>
              GO TO MY TEAM
            </button>
          </div>
        ) : null}
      </div>

      {stage < STAGES.bestXi ? (
        <p className="squad-reveal__tap">Tap anywhere to continue</p>
      ) : null}
    </div>
  );
}

export default SquadReveal;
